/**
 * EncouragementBanner — flash centered text like "GOOD!" / "GREAT!" / "ON FIRE!"
 *
 * One reusable Text object you call `show(label, color)` on whenever the
 * player hits a new combo tier (or any "celebrate this" moment). The
 * banner scales in (1.6 → 1, Back.out, 220ms), then fades up + out
 * (380ms delay, 700ms duration, Cubic.in). Existing tweens are killed
 * each call so rapid triggers don't stack.
 *
 * Extracted from horse-dash, puff-up, tap-tug — identical implementation.
 *
 * Usage:
 *   import { EncouragementBanner } from '@platform/sdk';
 *
 *   start() {
 *     this._encourage = new EncouragementBanner({ scene: this });
 *   }
 *
 *   _onComboTierUp(tier) {
 *     this._encourage.show(tier.label, tier.color);
 *   }
 */
export class EncouragementBanner {
  /**
   * @param {object} opts
   * @param {Phaser.Scene} opts.scene
   * @param {number} [opts.x]            defaults to scene center
   * @param {number} [opts.y]            defaults to ~50% of scene height (the banner rises from here)
   * @param {string} [opts.fontSize='64px']
   * @param {string} [opts.stroke='#000000']
   * @param {number} [opts.strokeThickness=8]
   * @param {number} [opts.depth=310]
   * @param {number} [opts.riseDelta]    how far the text rises during fade-out (px). Defaults to 8% of scene height.
   */
  constructor(opts) {
    const scene = opts.scene;
    this.scene = scene;

    const x = opts.x != null ? opts.x : scene.scale.width / 2;
    this._baseY = opts.y != null ? opts.y : scene.scale.height * 0.50;
    this._riseDelta = opts.riseDelta != null
      ? opts.riseDelta
      : scene.scale.height * 0.08;
    this._stroke = opts.stroke || '#000000';
    this._strokeThickness = opts.strokeThickness != null ? opts.strokeThickness : 8;

    this.text = scene.add.text(x, this._baseY, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: opts.fontSize || '64px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: this._stroke,
      strokeThickness: this._strokeThickness,
      align: 'center'
    }).setOrigin(0.5).setDepth(opts.depth != null ? opts.depth : 310).setAlpha(0);
  }

  /**
   * Trigger the banner.
   * @param {string} text  what to show
   * @param {number} [color=0xffffff]  text color (24-bit hex)
   */
  show(text, color = 0xffffff) {
    if (!this.text) return;
    this.text.setText(text);
    this.text.setColor('#' + color.toString(16).padStart(6, '0'));
    this.text.setStroke(this._stroke, this._strokeThickness);
    this.scene.tweens.killTweensOf(this.text);
    this.text.setAlpha(1);
    this.text.setScale(1.6);
    this.text.y = this._baseY;
    this.scene.tweens.add({
      targets: this.text,
      scale: 1,
      duration: 220,
      ease: 'Back.out'
    });
    this.scene.tweens.add({
      targets: this.text,
      y: this._baseY - this._riseDelta,
      alpha: 0,
      duration: 700,
      delay: 380,
      ease: 'Cubic.in'
    });
  }

  /** Tear down. Call on scene shutdown. */
  destroy() {
    if (this.scene && this.text) {
      this.scene.tweens.killTweensOf(this.text);
      this.text.destroy();
    }
    this.text = null;
    this.scene = null;
  }
}
